import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetBundleQuantity } from "../../redux/actions/dashboard";
import * as localService from "../../services/localService";
import apiService from "../../services/apiService";
import logger from "../../utils/logger";
import { packageSelectDataSignUp } from "../../data/package-select-data";
import { AuthLayout } from "../../components/layouts/AuthLayout";

// Simple Package Card Component
const SimplePackageCard = ({ packageData, onSelect, isSelected, organizationSize }) => {
  const currency = localService.getCurrency();

  return (
    <div
      className={`bg-white rounded-2xl border-2 shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${
        isSelected ? "border-blue-600 bg-blue-50" : "border-gray-200 hover:border-gray-300"
      }`}
      onClick={onSelect}
    >
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{packageData.title}</h3>
        <p className="text-sm text-gray-600">{packageData.description}</p>
      </div>

      <div className="text-center mb-6">
        <div className="text-3xl font-bold text-blue-600">
          {currency} {packageData.price?.toLocaleString() || "Contact Us"}
        </div>
        {packageData.billingPeriod && <p className="text-sm text-gray-500">per {packageData.billingPeriod}</p>}
      </div>

      {packageData.features && (
        <ul className="space-y-2 mb-6">
          {packageData.features.slice(0, 5).map((feature, index) => (
            <li key={index} className="flex items-center text-sm">
              <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 ${
          isSelected ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
      >
        {isSelected ? "Selected" : "Select Plan"}
      </button>
    </div>
  );
};

const SelectPackage = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // @ts-ignore
  let bundleTypeAndQuantities = useSelector(state => state.dashboard.bundleTypeAndQuantities);

  // @ts-ignore
  const { userSessionForBundles: user } = useSelector(state => state.auth);

  const [bundlePackages, setBundlePackages] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [ownerAccount, setOwnerAccount] = useState(null);
  const [totalOrderAmount, setTotalOrderAmount] = useState(0);
  const [currency, setCurrency] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(null);

  const organizationSize = useMemo(() => parseInt(localStorage.getItem("organizationSize") || "-1"), []);

  const getPreferredBundleType = () => {
    let preferredBundleType = "LOW";

    if (organizationSize >= 20) {
      preferredBundleType = "HIGH";
    } else if (organizationSize > 10 && organizationSize < 20) {
      preferredBundleType = "MEDIUM";
    }

    return preferredBundleType;
  };

  // Get all bundles on system
  useEffect(() => {
    setIsLoading(true);
    apiService
      .getAllBundles()
      .then(res => {
        const { data } = res.data;

        if (data && data.length > 0) {
          const preferredBundleType = getPreferredBundleType();

          bundleTypeAndQuantities = data.filter(bundle => bundle.bundleType.value === preferredBundleType);

          dispatch(resetBundleQuantity(bundleTypeAndQuantities));
          setBundles(bundleTypeAndQuantities);
          setTotalOrderAmount((bundleTypeAndQuantities[0]?.pricing?.cost || 0) * organizationSize);
        }
      })
      .catch(err => {
        logger.error("Error fetching bundles:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (bundles.length > 0) {
      const firstOne = bundles[0];
      const pricing = firstOne?.pricing;
      const currency = pricing?.currency;

      setCurrency(currency);
    }
  }, [bundles]);

  useEffect(() => {
    const currentUser = localService.getUser();
    setOwnerAccount(currentUser);
  }, []);

  const confirmOrder = () => {
    const ownerId = ownerAccount?.id;

    const bundleRequest = {
      ownerId,
      bundleTypeAndQuantityList: [
        {
          bundleType: bundleTypeAndQuantities[0].bundleType.value,
          quantity: organizationSize,
        },
      ],
    };

    apiService
      .getBundlePurchaseOrderSummary(bundleRequest)
      .then(res => {
        const response = res.data;
        const responseData = response?.data;
        logger.log("confirmOrder RESPONSE:", response);

        // Save in local storage
        localService.setConfirmOrderResponse(responseData);

        // Navigate to order summary
        history.push("/package-order-summary");
      })
      .catch(err => {
        logger.error("Error confirming order:", err);
      });
  };

  const handlePackageSelect = (selectedPackage, index) => {
    // Handle package selection logic
    logger.log("Package selected:", selectedPackage);
    setSelectedPackageIndex(index);
  };

  const handleContinue = () => {
    if (selectedPackageIndex !== null) {
      confirmOrder();
    }
  };

  if (isLoading) {
    return (
      <AuthLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="min-h-screen py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Select Your Package</h1>
            <p className="text-lg text-gray-600">Choose the perfect plan for your organization</p>
            {organizationSize > 0 && (
              <p className="text-sm text-gray-500 mt-2">Organization size: {organizationSize} members</p>
            )}
          </div>

          {/* Package Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {packageSelectDataSignUp.packages.map((packageData, index) => (
              <SimplePackageCard
                key={index}
                packageData={packageData}
                onSelect={() => handlePackageSelect(packageData, index)}
                isSelected={selectedPackageIndex === index}
                organizationSize={organizationSize}
              />
            ))}
          </div>

          {/* Order Summary */}
          {totalOrderAmount > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Amount:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {currency} {totalOrderAmount.toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => history.goBack()}
              className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition duration-200"
            >
              Go Back
            </button>
            <button
              onClick={handleContinue}
              disabled={selectedPackageIndex === null}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              Continue to Order Summary
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SelectPackage;
